// "Have Neo study the boat's …" on the Equipment page (TOM-615, cloud ADR
// 0379, api_contracts §5.21): which parts of the boat Neo studies on its own,
// whether it is studying one now, and an owner's press of the button. A study
// costs money, inside a budget the cloud holds; a press it turns down is said
// plainly here.

import { api } from '../api';
import { BOAT_NOTE_AREAS, NOTE_TOPICS, type BoatNoteArea } from './boatNotes';

/** One part of the boat Neo studies, as `GET …/notebook/study` lists it. */
export interface BoatStudyArea {
  area: BoatNoteArea;
  running: boolean;
  /** When its last successful study finished, or null. */
  lastStudiedAt: string | null;
  /** How its latest study ended, or null while one runs or when none has. */
  lastStatus: 'succeeded' | 'failed' | 'refused' | null;
}

const ENDINGS = new Set(['succeeded', 'failed', 'refused']);

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const AREAS = new Set<string>(BOAT_NOTE_AREAS);

/** `GET …/notebook/study`, or null when the body is not one. An entry it cannot read is skipped. */
export function parseBoatStudy(raw: unknown): BoatStudyArea[] | null {
  if (!isObject(raw) || !Array.isArray(raw.areas)) return null;
  return raw.areas.flatMap((a) => {
    if (!isObject(a) || typeof a.area !== 'string' || !AREAS.has(a.area)) return [];
    return [
      {
        area: a.area as BoatNoteArea,
        running: a.running === true,
        lastStudiedAt: typeof a.lastStudiedAt === 'string' ? a.lastStudiedAt : null,
        lastStatus:
          typeof a.lastStatus === 'string' && ENDINGS.has(a.lastStatus)
            ? (a.lastStatus as BoatStudyArea['lastStatus'])
            : null,
      },
    ];
  });
}

export async function fetchBoatStudy(boatId: string): Promise<BoatStudyArea[]> {
  const areas = parseBoatStudy(await api(`/v1/boats/${boatId}/notebook/study`));
  if (areas === null) throw new Error('notebook study: unexpected body');
  return areas;
}

export type BoatStudyPress =
  | { kind: 'started' }
  /** The cloud turned it down: its code, and for the boat's budget when it has room again. */
  | { kind: 'refused'; code: string; roomAt: string | null }
  | { kind: 'failed' };

/**
 * Ask Neo to study one part of the boat. The caller passes a fresh idempotency
 * key for each press; the page makes one per click and never resends.
 */
export async function askBoatStudy(
  boatId: string,
  area: BoatNoteArea,
  idempotencyKey: string
): Promise<BoatStudyPress> {
  try {
    await api(`/v1/boats/${boatId}/notebook/study`, {
      method: 'POST',
      body: { area },
      idempotencyKey,
    });
    return { kind: 'started' };
  } catch (err) {
    const status = isObject(err) && typeof err.status === 'number' ? err.status : null;
    const body = isObject(err) && isObject(err.body) ? err.body : null;
    const code = body !== null && typeof body.error === 'string' ? body.error : null;
    if ((status === 409 || status === 422) && code !== null) {
      return {
        kind: 'refused',
        code,
        roomAt: body !== null && typeof body.roomAt === 'string' ? body.roomAt : null,
      };
    }
    return { kind: 'failed' };
  }
}

/** The part of the boat in a sentence: "batteries and charging". */
export function studyTopic(area: BoatNoteArea): string {
  const heading = NOTE_TOPICS.find((t) => t.area === area)?.heading ?? area;
  return heading.toLowerCase();
}

/** The button: "Have Neo study the boat’s batteries and charging". */
export function studyButtonLabel(area: BoatNoteArea): string {
  return `Have Neo study the boat’s ${studyTopic(area)}`;
}

/** What the page says while a study runs. */
export function studyRunningText(area: BoatNoteArea): string {
  return `Neo is studying the boat’s ${studyTopic(area)}. What it learns shows up here when it’s done, usually in a minute or two.`;
}

/** What the page says when a study it saw running has ended. */
export function studyEndedText(
  area: BoatNoteArea,
  lastStatus: BoatStudyArea['lastStatus']
): string {
  return lastStatus === 'succeeded'
    ? `Neo finished studying the boat’s ${studyTopic(area)}. Anything it learned is in the notes below.`
    : 'Neo couldn’t finish studying this time. You can try again later.';
}

/** "Last studied Oct 12", under the button. */
export function studiedText(lastStudiedAt: string, now: Date = new Date()): string {
  return `Last studied ${day(lastStudiedAt, now)}`;
}

/** "Oct 30", or "Oct 30, 2027" outside this year. */
function day(iso: string, now: Date): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** The sentence for a press the cloud turned down, or that failed. */
export function studyRefusalText(
  area: BoatNoteArea,
  press: Exclude<BoatStudyPress, { kind: 'started' }>,
  now: Date = new Date()
): string {
  if (press.kind === 'failed') {
    return 'Couldn’t ask Neo to study the boat. Check the connection and try again.';
  }
  switch (press.code) {
    case 'study_running':
      return `Neo is already studying the boat’s ${studyTopic(area)}.`;
    case 'study_boat_budget':
      return press.roomAt !== null
        ? `Neo has studied this boat as much as it can for now. It can study it again from ${day(press.roomAt, now)}.`
        : 'Neo has studied this boat as much as it can for now. Try again in a few days.';
    case 'study_daily_budget':
      return 'Neo has done all the studying it can for today. Try again tomorrow.';
    case 'boat_quiet':
      return 'The boat hasn’t sent any readings in the last 30 days, so there’s nothing new to study.';
    case 'boat_inactive':
      return 'This boat isn’t in service, so Neo can’t study it.';
    default:
      return 'Couldn’t ask Neo to study the boat. Try again later.';
  }
}
